<script setup lang="ts">
import {computed, nextTick, ref, useTemplateRef, watch} from "vue";
import Str from "@/Utils/Str.ts";
import {Avatar, Button, Dialog, Menu} from "@/Components/Library";
import {Comments} from "@/Components/Global";
import dayjs from "dayjs";
import User from "@/Models/User.ts";
import {Icon} from "@iconify/vue";

const $user = new User();
const props = defineProps<{
    response: {[key: string]: any};
    question: {[key: string]: any};
    disabled?: boolean;
    expandComments?: boolean;
    highlightCommentId?: string | number | null;
}>();
// console.log(props.response);
const emit = defineEmits<{
    comment: any;
    vote: any;
    responseClick: any;
    edit: any;
    delete: any;
    deleteComment: any;
}>();
const voteCount = computed(() => Object.values(props.response.data?.votes ?? {}).reduce((acc, vote) => acc + vote, 0));
const userVote = computed(() => props.response.data?.votes?.[$user.id]);
const comments = useTemplateRef("comments");
const showComments = ref(!!props.response.comments_count || !!props.highlightCommentId);

// Watch for changes in expandComments prop
watch(
    () => props.expandComments,
    (newValue) => {
        if (newValue !== undefined) {
            showComments.value = newValue;
        }
    },
    {immediate: false},
);

// Watch for highlightCommentId to ensure comment thread is visible
watch(
    () => props.highlightCommentId,
    (newValue) => {
        if (newValue) {
            showComments.value = true;
        }
    },
    {immediate: true},
);
const showResponseMenu = ref(false);
const responseMenuItems = ref([
    {
        label: "Edit",
        icon: "prime:pencil",
        command: () => {
            emit("edit", props.response);
            showResponseMenu.value = false;
        },
    },
    {
        label: "Delete",
        icon: "prime:trash",
        class: "text-red-600 hover:bg-red-100",
        command: () => {
            emit("delete", props.response);
            showResponseMenu.value = false;
        },
    },
]);
const content = useTemplateRef("content");
// const $echo = useEcho([
//     {
//         event: "Comments\\CommentCreated",
//         callback: async (e: {question: string; comment: string; commentable: any}) => {
//             if (e.commentable.id !== props.response.id || e.commentable.type !== "App\\Models\\Response") {
//                 return;
//             }
//             addComment(e.comment);
//         },
//     },
// ]);
const reply = async () => {
    showComments.value = true;
    await nextTick();
    console.log(comments.value.commentTextarea);
    setTimeout(() => {
        comments.value.commentTextarea?.$el.scrollIntoView({
            behavior: "smooth",
            block: "center",
        });
        setTimeout(() => {
            comments.value.commentTextarea?.$el.focus();
        }, 200);
    }, 200);
};
defineExpose({
    content,
});
</script>
<template>
    <div>
        <div class="flex items-stretch gap-2">
            <div class="flex flex-col items-center gap-2 py-1" v-if="question.params.allow_voting">
                <Button
                    :disabled="response.user_id === $user.id"
                    @click="emit('vote', response, 1)"
                    class="h-8 w-8 rounded-full !p-1 shadow-sm !ring-0"
                    :class="[
                        userVote === 1
                            ? 'bg-green-600 text-white'
                            : 'border bg-stone-50 hover:bg-green-600 hover:text-white dark:border-stone-700 dark:bg-stone-900',
                    ]"
                    :icon="{icon: 'mingcute:arrow-up-fill', width: 20}"
                />
                <span class="text-lg font-bold">{{ voteCount }}</span>
                <!--                <Button-->
                <!--                    :disabled="response.user_id === $user.id"-->
                <!--                    @click="emit('vote', response, -1)"-->
                <!--                    class="h-8 w-8 rounded-full !p-1 shadow-sm !ring-0"-->
                <!--                    :class="[-->
                <!--                        getUserVote(response) === -1-->
                <!--                            ? 'bg-red-600 text-white'-->
                <!--                            : 'border bg-stone-50 hover:bg-red-600 hover:text-white',-->
                <!--                    ]"-->
                <!--                    :icon="{icon: 'mingcute:arrow-down-fill', width: 20}"-->
                <!--                />-->
            </div>
            <div
                class="flex w-full flex-col rounded-lg border bg-white px-4 py-3 dark:border-stone-700 dark:bg-stone-900"
                :class="[{'cursor-pointer': response.data?.annotation}]"
                ref="content"
                :id="response.id"
                @click.stop="emit('responseClick', response.id)"
            >
                <div class="flex h-full grow gap-3 rounded-lg">
                    <div class="flex grow flex-col">
                        <div class="mb-4 flex flex-wrap items-center gap-1 sm:gap-3">
                            <div class="order-last flex flex-none items-center gap-2 sm:order-first lg:mb-0">
                                <Avatar
                                    :user="response.user"
                                    class="h-8 w-8 flex-none rounded-full p-1 text-xs"
                                    shape="circle"
                                />
                                <h6 class="font-bold dark:text-stone-400">{{ response.user?.name }}</h6>
                            </div>
                            <div
                                class="order-first flex w-full items-center gap-1 sm:order-last sm:ml-auto sm:w-auto sm:gap-2"
                            >
                                <span class="flex-none text-xs">
                                    {{ dayjs(response.created_at).format("DD/MM/YYYY HH:mm") }}
                                </span>
                                <Button
                                    v-if="
                                        question.group?.can_admin ||
                                        (response.user_id === $user.id && question.isPublished)
                                    "
                                    type="button"
                                    :icon="{
                                        icon: 'pepicons-pencil:dots-y',
                                        width: '24',
                                    }"
                                    class="ml-auto h-8 w-8 flex-none !ring-0"
                                    @click.stop="showResponseMenu = true"
                                />
                            </div>
                        </div>
                        <div class="flex flex-col">
                            <audio
                                v-if="response.files?.[0]?.url"
                                :src="response.files?.[0]?.url"
                                controls
                                class="mb-3 w-full"
                            />
                            <div class="flex items-end">
                                <p
                                    class="prose prose-stone mr-2 max-w-full text-sm dark:prose-invert"
                                    v-html="
                                        Str.of(response.data?.content ?? '')
                                            .nl2br()
                                            .toString()
                                    "
                                />
                                <Button
                                    v-if="!disabled"
                                    @click.stop="reply"
                                    type="button"
                                    :icon="{
                                        icon: 'bi:reply-fill',
                                        width: 24,
                                    }"
                                    class="float-right"
                                />
                            </div>
                        </div>
                    </div>
                </div>
                <div class="mt-auto" v-auto-animate>
                    <hr class="my-3 dark:border-stone-800" />
                    <div class="flex items-center">
                        <div>
                            <button
                                type="button"
                                class="flex items-center gap-1 text-xs"
                                @click.stop="showComments = !showComments"
                            >
                                <Icon v-bind="{icon: 'heroicons-solid:chat-alt', width: 20}" />
                                {{ showComments ? "Hide" : "Show" }} comments
                                <span
                                    class="min-w-[22px] rounded-full border bg-stone-600 px-1.5 py-0.5 text-white dark:border-stone-800 dark:bg-stone-900"
                                >
                                    {{ response.comments_count || 0 }}
                                </span>
                            </button>
                        </div>
                        <Button
                            v-if="response.data?.annotation"
                            :icon="{
                                icon: 'mdi:comment',
                                position: 'end',
                            }"
                            class="ml-auto !items-start !px-0 !py-0 text-xs font-normal !ring-0"
                        >
                            Go to annotation
                        </Button>
                    </div>
                    <Comments
                        ref="comments"
                        class="mt-3"
                        v-if="showComments"
                        :model="response"
                        model-type="response"
                        hide-title
                        @click.stop
                        :disabled="disabled"
                        :show-form="!disabled"
                        :allow-voice="question.params.allow_voice_comments"
                        :highlight-id="highlightCommentId"
                        @comment="emit('comment', $event)"
                        @delete="emit('deleteComment', $event)"
                        :can-admin="question.group?.can_admin"
                    />
                </div>
            </div>
        </div>
        <Dialog
            v-model:visible="showResponseMenu"
            modal
            dismissable-mask
            class="max-w-xs overflow-hidden"
            :closable="false"
            :show-header="false"
        >
            <template #container>
                <div class="bg-white">
                    <Menu ref="menu" :model="responseMenuItems" />
                </div>
            </template>
        </Dialog>
    </div>
</template>
<style scoped></style>
